
const getRelativeOffset = (element: HTMLElement) => {
  const parent = element.parentElement;
  if(!parent) { return undefined; }

  const rect = element.getBoundingClientRect();
  const pRect = parent.getBoundingClientRect();
  return {
    top: rect.top - pRect.top,
    left: rect.left - pRect.left,
  };
}

/**
 * Gets the position of the top side of the top border of the target element relative to
 * the bottom side of its given ancestor's top border.
 * @param element target element
 * @param ancestor ancestor element
 * @returns offset from top of ancestor, unless the given element, the given ancestor, or any intermediate ancestor is not visible (display: none),
 * or if the target element is not a child of the given ancestor.  In such cases, returns undefined.
 */
export const getVerticalOffsetFromAncestor = (element: HTMLElement, ancestor: HTMLElement) => {
  const elStyle = window.getComputedStyle(element);
  if(elStyle.display === 'none') { return undefined; }

  let prevOffset = 0;
  let child = element;
  let parent = element.parentElement;
  if(!parent) { return undefined; }

  while(!!parent) {
    if(ancestor !== parent && parent === window.document.body) { 
      return undefined; // we're done looking at this point
    }
    const parStyle = window.getComputedStyle(parent);
    if(parStyle.display === 'none') { return undefined; }

    const objOffset = getRelativeOffset(child)!;
    const offset = objOffset.top + prevOffset;
    if(parent === ancestor) { return offset; }

    prevOffset = offset;
    child = parent;
    parent = child.parentElement;
  }
  return undefined;
}

const isWithinAncestorViewport = (element: HTMLElement, ancestor: HTMLElement) => {
  const eTop = getVerticalOffsetFromAncestor(element, ancestor);
  if(eTop === undefined) { return false; }
  const eBottom = element.getBoundingClientRect().height + eTop;
  const vpHeight = ancestor.getBoundingClientRect().height;

  if(eTop >= 0 && eTop < vpHeight) { return true; }
  if(eBottom > 0 && eBottom <= vpHeight) { return true; }
  if(eTop <= 0 && eBottom >= vpHeight) { return true; }
  return false;
}

export const isVisibleWithinAncestorViewport = (element: HTMLElement, ancestor: HTMLElement) => {
  const elStyle = window.getComputedStyle(element);
  if(elStyle.display === 'none') { return false; }

  let parent = element.parentElement;
  if(!parent) { return false; }

  while(!!parent) {
    if(ancestor !== parent && parent === window.document.body) { 
      return false; // target is not a descendant of ancestor
    }
    const parStyle = window.getComputedStyle(parent);
    if(parStyle.display === 'none') { return false; }

    const visible = isWithinAncestorViewport(element, parent);
    if(visible === false) { return false; }
    if(ancestor === parent) { return true; }

    parent = parent.parentElement;
  }

  return false;
}